import React, { useState, useEffect } from 'react';
import { X, Save, Loader2 } from 'lucide-react';
import { useSystemPreset } from '../../contexts/SystemPresetContext';
import { paymentService } from '../../services/paymentService';
import type { Payment } from '../../types';
import type { LegalFile } from '../../types';

interface AddPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedFile: LegalFile;
  onAddPayment: (paymentData: Payment) => void;
  user: any;
  selectedRole: any;
  language: 'en' | 'ar';
  strings: any;
}

const AddPaymentModal: React.FC<AddPaymentModalProps> = ({
  isOpen,
  onClose,
  selectedFile,
  onAddPayment,
  user,
  selectedRole,
  language,
  strings
}) => {
  useSystemPreset();
  const [formData, setFormData] = useState({
    value: '',
    currency: 'EGP',
    paymentMethod: 'cash',
    status: 'completed' as 'completed' | 'pending' | 'failed' | 'cancelled',
    paymentDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    description: '',
    paymentTypeId: '1', // 1 for income, 2 for expense
    isIncome: true,
    invoiceNumber: '',
    receiptNumber: '',
    notes: '',
    attachmentURL: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        value: '',
        currency: 'EGP',
        paymentMethod: 'cash',
        status: 'completed',
        paymentDate: new Date().toISOString().split('T')[0],
        dueDate: '',
        description: '',
        paymentTypeId: '1',
        isIncome: true,
        invoiceNumber: '',
        receiptNumber: '',
        notes: '',
        attachmentURL: ''
      });
      setError('');
    }
  }, [isOpen]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePaymentTypeChange = (paymentTypeId: string) => {
    const isIncome = paymentTypeId === '1';
    setFormData(prev => ({
      ...prev,
      paymentTypeId,
      isIncome
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.value || !formData.description) {
      setError(language === 'ar' ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill in all required fields');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const paymentData: Omit<Payment, 'id' | 'createdAt' | 'updatedAt'> = {
        value: parseFloat(formData.value),
        currency: formData.currency,
        paymentMethod: formData.paymentMethod,
        status: formData.status,
        paymentDate: new Date(formData.paymentDate).getTime().toString(),
        dueDate: formData.dueDate ? new Date(formData.dueDate).getTime().toString() : '',
        description: formData.description,
        clientID: selectedFile.clientID || '',
        clientName: selectedFile.clientName || '',
        clientPhoneNumber: selectedFile.clientPhoneNumber || selectedFile.clientMobileNumber || '',
        clientMobileNumber: selectedFile.clientMobileNumber || '',
        clientPhotoURL: selectedFile.clientPhotoURL || '',
        legalFileID: selectedFile.id,
        legalFileTitle: selectedFile.title,
        legalFileId: selectedFile.id,
        caseID: selectedFile.caseID || '',
        caseNumber: selectedFile.caseNumber || '',
        caseYear: selectedFile.caseYear || '',
        courtTypeID: selectedFile.courtTypeID || '',
        caseFirstSessionDate: selectedFile.caseFirstSessionDate || '',
        caseOpponents: selectedFile.caseOpponents || '',
        expectedFees: selectedFile.expectedFees || 0,
        notes: formData.notes,
        lawyerID: user?.id || '',
        lawyerName: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '',
        lawyerPhotoURL: user?.photoURL || '',
        lawyerLogo: user?.photoURL || '',
        officeID: selectedRole?.officeID || '',
        officeName: selectedRole?.officeName || '',
        officeLogoURL: selectedRole?.officeLogoURL || '',
        officeAddress: selectedRole?.officeAddress || '',
        officeEmail: selectedRole?.officeEmail || '',
        officePhone: selectedRole?.officePhone || '',
        officeLogo: selectedRole?.officeLogo || '',
        roleID: selectedRole?.id || '',
        paymentTypeId: formData.paymentTypeId,
        isIncome: formData.isIncome,
        attachmentURL: formData.attachmentURL,
        invoiceNumber: formData.invoiceNumber,
        receiptNumber: formData.receiptNumber,
        isActive: true,
        isArchived: false,
        isDeleted: false,
        createdBy: user?.id || '',
        updatedBy: user?.id || ''
      };

      const newPayment = await paymentService.addPayment(paymentData);
      onAddPayment(newPayment);
      onClose();
    } catch (error) {
      console.error('Error adding payment:', error);
      setError(language === 'ar' ? 'حدث خطأ أثناء إضافة الدفعة' : 'Error adding payment');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="p-6 border-b border-slate-200 dark:border-slate-700 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-700 rounded-xl flex items-center justify-center text-white font-semibold text-lg">
                💰
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-800 dark:text-white">
                  {language === 'ar' ? 'إضافة دفعة جديدة' : 'Add New Payment'}
                </h2>
                <p className="text-slate-600 dark:text-slate-400">
                  {selectedFile.title}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors duration-200"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Error Message */}
            {error && (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400">
                {error}
              </div>
            )}

            {/* Payment Type */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                {language === 'ar' ? 'نوع الدفعة' : 'Payment Type'}
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => handlePaymentTypeChange('1')}
                  className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                    formData.paymentTypeId === '1'
                      ? 'hover:shadow-lg hover:scale-105'
                      : 'border-slate-200 dark:border-slate-600 hover:shadow-md'
                  }`}
                  style={{
                    borderColor: formData.paymentTypeId === '1' ? '#2E7D32' : undefined,
                    backgroundColor: formData.paymentTypeId === '1' ? '#E8F5E8' : undefined,
                  }}
                  onMouseEnter={(e) => {
                    if (formData.paymentTypeId === '1') {
                      e.currentTarget.style.backgroundColor = '#C8E6C9';
                      e.currentTarget.style.borderColor = '#1B5E20';
                    } else {
                      e.currentTarget.style.backgroundColor = '#F1F8E9';
                      e.currentTarget.style.borderColor = '#4CAF50';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (formData.paymentTypeId === '1') {
                      e.currentTarget.style.backgroundColor = '#E8F5E8';
                      e.currentTarget.style.borderColor = '#2E7D32';
                    } else {
                      e.currentTarget.style.backgroundColor = '';
                      e.currentTarget.style.borderColor = '';
                    }
                  }}
                >
                  <div className="text-center">
                    <div className="text-2xl mb-2">💰</div>
                    <div className="font-semibold text-slate-800 dark:text-white">
                      {language === 'ar' ? 'دخل' : 'Income'}
                    </div>
                    <div className="text-sm text-slate-500 dark:text-slate-400">
                      {language === 'ar' ? 'دفعة من العميل' : 'Payment from client'}
                    </div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => handlePaymentTypeChange('2')}
                  className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                    formData.paymentTypeId === '2'
                      ? 'hover:shadow-lg hover:scale-105'
                      : 'border-slate-200 dark:border-slate-600 hover:shadow-md'
                  }`}
                  style={{
                    borderColor: formData.paymentTypeId === '2' ? '#D32F2F' : undefined,
                    backgroundColor: formData.paymentTypeId === '2' ? '#FFEBEE' : undefined,
                  }}
                  onMouseEnter={(e) => {
                    if (formData.paymentTypeId === '2') {
                      e.currentTarget.style.backgroundColor = '#FFCDD2';
                      e.currentTarget.style.borderColor = '#B71C1C';
                    } else {
                      e.currentTarget.style.backgroundColor = '#FFE0E0';
                      e.currentTarget.style.borderColor = '#F44336';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (formData.paymentTypeId === '2') {
                      e.currentTarget.style.backgroundColor = '#FFEBEE';
                      e.currentTarget.style.borderColor = '#D32F2F';
                    } else {
                      e.currentTarget.style.backgroundColor = '';
                      e.currentTarget.style.borderColor = '';
                    }
                  }}
                >
                  <div className="text-center">
                    <div className="text-2xl mb-2">💸</div>
                    <div className="font-semibold text-slate-800 dark:text-white">
                      {language === 'ar' ? 'مصروف' : 'Expense'}
                    </div>
                    <div className="text-sm text-slate-500 dark:text-slate-400">
                      {language === 'ar' ? 'دفعة للعميل' : 'Payment to client'}
                    </div>
                  </div>
                </button>
              </div>
            </div>

            {/* Amount and Currency */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {language === 'ar' ? 'المبلغ' : 'Amount'} *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.value}
                  onChange={(e) => handleInputChange('value', e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white"
                  placeholder={language === 'ar' ? 'أدخل المبلغ' : 'Enter amount'}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {language === 'ar' ? 'العملة' : 'Currency'}
                </label>
                <select
                  value={formData.currency}
                  onChange={(e) => handleInputChange('currency', e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white"
                >
                  <option value="EGP">EGP</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                {language === 'ar' ? 'الوصف' : 'Description'} *
              </label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white"
                placeholder={language === 'ar' ? 'أدخل وصف الدفعة' : 'Enter payment description'}
                required
              />
            </div>

            {/* Payment Method and Status */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {language === 'ar' ? 'طريقة الدفع' : 'Payment Method'}
                </label>
                <select
                  value={formData.paymentMethod}
                  onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white"
                >
                  <option value="cash">{language === 'ar' ? 'نقداً' : 'Cash'}</option>
                  <option value="bank_transfer">{language === 'ar' ? 'تحويل بنكي' : 'Bank Transfer'}</option>
                  <option value="check">{language === 'ar' ? 'شيك' : 'Check'}</option>
                  <option value="credit_card">{language === 'ar' ? 'بطاقة ائتمان' : 'Credit Card'}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {language === 'ar' ? 'الحالة' : 'Status'}
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white"
                >
                  <option value="completed">{language === 'ar' ? 'مكتمل' : 'Completed'}</option>
                  <option value="pending">{language === 'ar' ? 'معلق' : 'Pending'}</option>
                  <option value="failed">{language === 'ar' ? 'فشل' : 'Failed'}</option>
                  <option value="cancelled">{language === 'ar' ? 'ملغي' : 'Cancelled'}</option>
                </select>
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {language === 'ar' ? 'تاريخ الدفع' : 'Payment Date'}
                </label>
                <input
                  type="date"
                  value={formData.paymentDate}
                  onChange={(e) => handleInputChange('paymentDate', e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {language === 'ar' ? 'تاريخ الاستحقاق' : 'Due Date'}
                </label>
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => handleInputChange('dueDate', e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white"
                />
              </div>
            </div>

            {/* Invoice and Receipt Numbers */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {language === 'ar' ? 'رقم الفاتورة' : 'Invoice Number'}
                </label>
                <input
                  type="text"
                  value={formData.invoiceNumber}
                  onChange={(e) => handleInputChange('invoiceNumber', e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white"
                  placeholder={language === 'ar' ? 'رقم الفاتورة' : 'Invoice number'}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {language === 'ar' ? 'رقم الإيصال' : 'Receipt Number'}
                </label>
                <input
                  type="text"
                  value={formData.receiptNumber}
                  onChange={(e) => handleInputChange('receiptNumber', e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white"
                  placeholder={language === 'ar' ? 'رقم الإيصال' : 'Receipt number'}
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                {language === 'ar' ? 'ملاحظات' : 'Notes'}
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                rows={3}
                className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white resize-none"
                placeholder={language === 'ar' ? 'ملاحظات إضافية' : 'Additional notes'}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-slate-200 dark:border-slate-700">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors duration-200"
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex items-center space-x-2 px-6 py-3 bg-primary text-white rounded-xl hover:bg-primary-dark transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>{language === 'ar' ? 'جاري الحفظ...' : 'Saving...'}</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>{language === 'ar' ? 'حفظ الدفعة' : 'Save Payment'}</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddPaymentModal;
